import React from 'react';
import { useSafeLayoutEffect } from '@components-shared/src/hooks/useSafeLayoutEffect';
import type { CSS } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { Timestamp } from '@components/basics/Timestamp';
import { MediaQuery } from '@context/mediaQuery';

const MAX_MOBILE_HEIGHT = 40;
const MAX_LAPTOP_HEIGHT = 50;

export const ListContent = ({
  children,
  isoDateString,
  truncateChildren = false,
  forceMobileView = false,
}: {
  children: React.ReactNode;
  isoDateString: string;
  truncateChildren?: boolean;
  forceMobileView?: boolean;
}) => {
  const [overflow, setOverflow] = React.useState(false);
  const textRef = React.useRef<HTMLParagraphElement>(null);
  const { tabletLandscape } = MediaQuery.useContainer();

  const maxHeight = tabletLandscape ? MAX_LAPTOP_HEIGHT : MAX_MOBILE_HEIGHT;

  //TO DO: optimize this https://linear.app/sound/issue/ENG-3444/optimization-find-a-better-way-to-smartly-truncate-text-in-list
  useSafeLayoutEffect(() => {
    if (truncateChildren && textRef.current) {
      const { clientHeight } = textRef.current;
      setOverflow(clientHeight > maxHeight + 1);
    }
  }, [maxHeight, textRef, truncateChildren]);

  return (
    <Content ref={textRef}>
      <InfoWrapper forceMobileView={forceMobileView} overflow={overflow} lineClamp={overflow}>
        {children}
      </InfoWrapper>
      <Timestamp timeStamp={isoDateString} className="!text-base-xs text-base400" />
    </Content>
  );
};

const Content = styled('div', {
  display: 'flex',
  lineHeight: '18px',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: 8,

  '@tablet': {
    lineHeight: 'unset',
    gap: 'unset',
  },
});

const OVERFLOW_CSS: CSS = {
  whiteSpace: 'nowrap',
  maxWidth: 90,
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  '@tabletLandscape': {
    maxWidth: 150,
  },

  '@laptop': {
    maxWidth: 200,
  },
};

const InfoWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: '$base',
  color: '$neutral500',
  maxWidth: '100%',

  variants: {
    forceMobileView: {
      false: {
        '@tablet': {
          flexWrap: 'nowrap',
          display: 'inline-flex',
          overflow: 'visible',
        },
      },
    },
    overflow: {
      true: {},
    },
    lineClamp: {
      true: {},
    },
  },

  compoundVariants: [
    {
      forceMobileView: false,
      lineClamp: true,
      css: {
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',

        '@tabletLandscape': {
          display: 'flex',
        },
      },
    },
    {
      forceMobileView: false,
      overflow: true,
      css: {
        '.release-title': {
          ...OVERFLOW_CSS,
        },

        '.artist-name': {
          ...OVERFLOW_CSS,
        },

        '.playlist-name': {
          ...OVERFLOW_CSS,
        },
      },
    },
  ],
});
